/* eslint-disable @typescript-eslint/no-base-to-string */
import { useState } from "react";
import { routeHref } from "../../app/router";
import type {
  AbilityRecord,
  FieldManifest,
  ViewElement,
} from "../../shared/api/generated/contracts";
import { useLocale, type Translate } from "../../shared/i18n";
import {
  Badge,
  Button,
  Card,
  Chip,
  EmptyState,
  ListRow,
} from "../../shared/ui";
import { optionsOf, visibleFields } from "./fields";
import {
  GROUPABLE_TYPES,
  applyFilters,
  dayOf,
  groupRecords,
  optionColorOf,
  pageGroups,
  parseListConfig,
  recordsOfCollection,
  sortRecords,
} from "./listConfig";
import type { RendererContext } from "./registry";

/** Records the list shows before "Show more". */
const LIST_PAGE = 20;

/** Rows carry their own hairline; the last one in a list drops it. */
const LIST_CLASS = "[&>li:last-child>[data-row]]:after:hidden";

function formatDay(day: string, locale: string): string {
  const [year, month, date] = day.split("-").map(Number);
  return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(
    new Date(year ?? 0, (month ?? 1) - 1, date ?? 1),
  );
}

/** Human text of one stored value; user content is rendered as text only. */
function displayValue(
  field: FieldManifest,
  value: unknown,
  locale: string,
  t: Translate,
): string {
  if (value === undefined || value === null || value === "") return "";
  if (field.type === "boolean")
    return value === true ? t("list.group.yes") : t("list.group.no");
  if (field.type === "select")
    return (
      optionsOf(field).find((option) => option.value === value)?.label ??
      String(value)
    );
  if (field.type === "date" || field.type === "datetime") {
    const day = dayOf(value);
    if (day) return formatDay(day, locale);
  }
  if (Array.isArray(value)) return value.map(String).join(", ");
  return String(value);
}

function groupTitle(
  key: string,
  field: FieldManifest,
  locale: string,
  t: Translate,
): string {
  if (key === "") return t("list.group.none");
  if (field.type === "boolean")
    return key === "true" ? t("list.group.yes") : t("list.group.no");
  if (field.type === "select")
    return (
      optionsOf(field).find((option) => option.value === key)?.label ?? key
    );
  return /^\d{4}-\d{2}-\d{2}$/.test(key) ? formatDay(key, locale) : key;
}

export function RecordList({
  element,
  context,
}: {
  element: ViewElement;
  context: RendererContext;
}) {
  const { locale, t } = useLocale();
  const configured = element.config?.collection_id;
  const collectionId =
    typeof configured === "string"
      ? configured
      : (context.collectionId ?? context.records[0]?.collection_id);
  const collection = context.collections?.find(
    (candidate) => candidate.id === collectionId,
  );
  const shownFields = visibleFields(collection);
  const fieldOf = (id: string | undefined) =>
    id ? shownFields.find((field) => field.id === id) : undefined;
  const settings = parseListConfig(element.config);
  // The first select field with declared options is the facet candidate.
  const facetCandidate = shownFields.find(
    (field) => field.type === "select" && optionsOf(field).length > 0,
  );
  const [choice, setChoice] = useState<string | null>(null);
  const [shown, setShown] = useState(LIST_PAGE);
  const base = applyFilters(
    recordsOfCollection(context.records, collectionId),
    settings.filters,
  );
  // The facet row never repeats the configured grouping or filters, and only
  // offers options that still have records in the configured base set.
  const facetOptions =
    facetCandidate &&
    !settings.groupBy &&
    !settings.filters.some((filter) => filter.field_id === facetCandidate.id)
      ? optionsOf(facetCandidate).filter((option) =>
          base.some(
            (record) => record.payload[facetCandidate.id] === option.value,
          ),
        )
      : [];
  const facet = facetOptions.length >= 2 ? facetCandidate : undefined;
  const sortField = fieldOf(settings.sortBy);
  const filtered = sortRecords(
    facet && choice !== null
      ? base.filter((record) => record.payload[facet.id] === choice)
      : base,
    settings.sortBy,
    settings.sortDir,
    sortField?.type,
  );
  const pick = (value: string | null) => {
    setChoice(value);
    setShown(LIST_PAGE);
  };
  const subtitleField = fieldOf(settings.subtitleFieldId);
  const badgeField = fieldOf(settings.badgeFieldId);
  const groupField = fieldOf(settings.groupBy);
  // Group the whole filtered, sorted list, then page over the grouped order so
  // "Show more" only appends and never reshuffles groups.
  const groups =
    groupField && GROUPABLE_TYPES.includes(groupField.type)
      ? pageGroups(groupRecords(filtered, groupField), shown)
      : null;
  const visible = filtered.slice(0, shown);

  const renderRow = (record: AbilityRecord) => {
    const primary = collection
      ? record.payload[collection.primary_display_field_id]
      : undefined;
    const subtitle = subtitleField
      ? displayValue(subtitleField, record.payload[subtitleField.id], locale, t)
      : "";
    const badgeValue = badgeField ? record.payload[badgeField.id] : undefined;
    const badge =
      badgeField && badgeValue !== undefined && badgeValue !== ""
        ? displayValue(badgeField, badgeValue, locale, t)
        : "";
    return (
      <li key={record.id}>
        <ListRow
          title={String(primary ?? record.id)}
          subtitle={subtitle || undefined}
          badge={
            badge && badgeField ? (
              <Badge tone={optionColorOf(badgeField, badgeValue) ?? "neutral"}>
                {badge}
              </Badge>
            ) : undefined
          }
          marker="mine"
          onClick={
            context.abilityId && context.viewId
              ? () => {
                  context.navigate(
                    routeHref({
                      kind: "record",
                      abilityId: context.abilityId ?? "",
                      viewId: context.viewId ?? "",
                      recordId: record.id,
                    }),
                  );
                }
              : undefined
          }
        />
      </li>
    );
  };

  return (
    <Card flush title={String(element.config?.title ?? t("widget.records"))}>
      {base.length === 0 ? (
        <EmptyState
          title={t("empty.title")}
          body={settings.emptyText ?? t("empty.body")}
        />
      ) : (
        <>
          {facet && (
            <div
              role="group"
              aria-label={facet.label}
              className="flex gap-2 overflow-x-auto px-gutter pb-2"
            >
              <Chip
                selected={choice === null}
                onClick={() => {
                  pick(null);
                }}
              >
                {t("list.all")}
              </Chip>
              {facetOptions.map((option) => (
                <Chip
                  key={option.value}
                  selected={choice === option.value}
                  onClick={() => {
                    pick(option.value);
                  }}
                >
                  {option.label}
                </Chip>
              ))}
            </div>
          )}
          {filtered.length === 0 ? (
            <EmptyState
              title={t("list.filteredEmpty.title")}
              body={t("list.filteredEmpty.body")}
              action={
                <Button
                  variant="secondary"
                  onClick={() => {
                    pick(null);
                  }}
                >
                  {t("list.clearFilter")}
                </Button>
              }
            />
          ) : groups && groupField ? (
            groups.map((group) => (
              <section
                key={group.key}
                aria-label={groupTitle(group.key, groupField, locale, t)}
              >
                <h3 className="px-4 pb-1 pt-3 text-caption font-semibold uppercase tracking-caps text-fg-subtle">
                  {groupTitle(group.key, groupField, locale, t)}
                </h3>
                <ul className={LIST_CLASS}>{group.records.map(renderRow)}</ul>
              </section>
            ))
          ) : (
            <ul className={LIST_CLASS}>{visible.map(renderRow)}</ul>
          )}
          {filtered.length > shown && (
            <div className="p-3">
              <Button
                variant="ghost"
                className="w-full"
                onClick={() => {
                  setShown((count) => count + LIST_PAGE);
                }}
              >
                {t("list.more")}
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
}
